import { useState } from 'react';
import type { SiteApprovalLineMetaData } from '@safetyhub/shared';
import { Button } from '@safetyhub/ui';
import ApprovalLineMetaEditor from './ApprovalLineMetaEditor';
import MetaDiffView from './MetaDiffView';
import { useReportDirty } from '../../hooks/useChildDirty';

/**
 * 현장 override 카드 — 편집 가능한 축은 결재선(defaultApprovalLine)뿐이다.
 * 활성 여부·maxAdditionalLines는 양식(템플릿 approvalLineMeta) 단독 권위라 읽기 전용으로 보여 준다
 * (결정기록 2026-06-13) — 양식 쪽 편집은 Composer 에디터 양식 탭이다.
 * 현장 행에는 그 두 값의 컬럼 자체가 없다 — `meta`에 실린 값은 서버가 양식에서 채운 해소 결과라,
 * 조회·편집 어느 화면에서든 그 값의 **원천이 양식임**을 함께 표시하고 저장 body에는 싣지 않는다.
 * ⚠️ 「자동승인 역할」 select를 두지 말 것 — 결재 판정이 읽지 않는 스위치라 화면이 거짓 안내를 하게 된다.
 */
interface ApprovalLineMetaCardProps {
  meta: SiteApprovalLineMetaData;
  baseMeta?: SiteApprovalLineMetaData;
  showDiff?: boolean;
  disableMemberType?: boolean;
  /**
   * 카드 제목 — 호출측(`SiteApprovalLineTab`)이 템플릿 이름을 준다. 축 값이 Composer 템플릿 key라 카드가 스스로
   * 이름을 알 길이 없다(은퇴 키 라벨 폴백을 두지 않는다 — 그 맵은 변경 이력 모달만 쓴다).
   */
  displayLabel: string;
  /**
   * 활성 여부·maxAdditionalLines 읽기 전용 표시에 붙이는 **원천 안내** 문구.
   *
   * 현장 행에는 그 두 값의 컬럼이 없으므로 이것은 「누가 소유하는가」가 아니라 「이 값이 어디서
   * 왔는가」를 말한다 — 화면에는 `({이 문구})`·`({이 문구} — 현장별로 바꿀 수 없습니다)`로 나간다.
   */
  maxAdditionalLinesSourceLabel?: string;
  onSaveRequest: (meta: SiteApprovalLineMetaData) => void;
  /** 미저장 닫기 보호 — 편집 중인 결재선이 원본과 다르면 true를 올린다([[useReportDirty]]). */
  onDirtyChange?: (dirty: boolean) => void;
}

export default function ApprovalLineMetaCard({
  meta,
  baseMeta,
  showDiff = false,
  disableMemberType,
  displayLabel,
  maxAdditionalLinesSourceLabel = '양식(Composer) 설정',
  onSaveRequest,
  onDirtyChange,
}: ApprovalLineMetaCardProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<SiteApprovalLineMetaData>(meta);
  // 편집 가능한 축은 결재선뿐이므로 그 축만 원본(startEdit의 복제 기준)과 비교한다.
  useReportDirty(
    editing && JSON.stringify(draft.defaultApprovalLine) !== JSON.stringify(meta.defaultApprovalLine),
    onDirtyChange,
  );

  const startEdit = () => {
    setDraft({ ...meta, defaultApprovalLine: [...meta.defaultApprovalLine] });
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(meta);
    setEditing(false);
  };

  const requestSave = () => {
    onSaveRequest(draft);
    setEditing(false);
  };

  return (
    <div className="border border-gray-200 rounded-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-800 text-sm">{displayLabel}</h3>
          <span className="text-xs text-gray-400">v{meta.version}</span>
        </div>
        {!editing && (
          <Button variant="secondary" size="xs" onClick={startEdit}>
            편집
          </Button>
        )}
      </div>

      {!editing ? (
        <div className="space-y-2 text-sm">
          <div className="flex gap-4">
            <span className="text-gray-500">결재라인 정책</span>
            <span
              className={meta.hasApprovalLinePolicy ? 'text-green-600 font-medium' : 'text-gray-400'}
              title={`${maxAdditionalLinesSourceLabel} — 현장별로 바꿀 수 없습니다`}
            >
              {meta.hasApprovalLinePolicy ? '활성' : '비활성'}
              <span className="ml-1.5 text-xs font-normal text-gray-400">({maxAdditionalLinesSourceLabel})</span>
            </span>
          </div>
          {meta.hasApprovalLinePolicy && (
            <>
              <div className="flex gap-4">
                <span className="text-gray-500">결재 단계 수</span>
                <span>{meta.defaultApprovalLine.length}단계</span>
              </div>
              <div className="flex gap-4">
                <span className="text-gray-500">추가 결재 최대</span>
                <span title={`${maxAdditionalLinesSourceLabel} — 현장별로 바꿀 수 없습니다`}>
                  {meta.maxAdditionalLines}개
                  <span className="ml-1.5 text-xs text-gray-400">({maxAdditionalLinesSourceLabel})</span>
                </span>
              </div>
              <div>
                <p className="text-gray-500 mb-1">결재 단계</p>
                {meta.defaultApprovalLine.map((row) => (
                  <div key={row.level} className="text-xs bg-gray-50 rounded px-2 py-1 mb-1">
                    Lv.{row.level} {row.label} — {row.locked ? 'locked' : 'unlocked'} —{' '}
                    {row.assignmentChain.map((s) => {
                      if (s.type === 'role') return `역할:${s.role}`;
                      if (s.type === 'member') return `멤버:${s.memberId}`;
                      return s.type;
                    }).join(' → ')}
                  </div>
                ))}
              </div>
            </>
          )}
          {showDiff && baseMeta && (
            <div className="pt-2 border-t border-gray-100">
              <MetaDiffView current={meta} base={baseMeta} />
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/*
            활성여부는 양식(템플릿 approvalLineMeta) 단독 권위 — site scope에서는 읽기 전용
            (maxAdditionalLines와 동일, 결정기록 2026-06-13).
          */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">결재라인 정책</label>
            <span className="text-sm text-gray-700">
              {draft.hasApprovalLinePolicy ? '활성' : '비활성'}
              <span className="ml-1.5 text-xs text-gray-400">({maxAdditionalLinesSourceLabel}, 변경 불가)</span>
            </span>
          </div>

          {draft.hasApprovalLinePolicy && (
            <>
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700 w-32">추가 결재 최대</label>
                <span className="text-sm text-gray-700">
                  {draft.maxAdditionalLines}개
                  <span className="ml-1.5 text-xs text-gray-400">
                    ({maxAdditionalLinesSourceLabel}, 변경 불가)
                  </span>
                </span>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">결재 단계</label>
                <ApprovalLineMetaEditor
                  lines={draft.defaultApprovalLine}
                  onChange={(lines) => setDraft({ ...draft, defaultApprovalLine: lines })}
                  disableMemberType={disableMemberType}
                />
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" size="sm" onClick={cancelEdit}>
              취소
            </Button>
            <Button variant="primary" size="sm" onClick={requestSave}>
              저장 (사유 입력)
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
